// What app log lines may carry into a feedback report. A line is kept to its
// first line (pull request content, such as diffs, code and comment bodies,
// spans lines) and to MAX_LINE characters, and anything that looks like a
// credential is replaced by `[redacted]`. The server scrubs credentials again.

/** Longest log line kept, in characters. */
export const MAX_LINE = 300;

const TOKEN = /(?<![A-Za-z0-9_])(?:gh[pousr]_|github_pat_)[A-Za-z0-9_]{20,}/g;
/** Markers whose value runs to the end of the line. */
const HEADER = /\b(authorization|cookie|set-cookie|x-changelist-user)(:\s*)[^\r\n"']+/gi;
/** Markers whose value is one word. */
const WORD = /(\bbearer\s+|cl_session=|cl_oauth=)[^\s;,"']+/gi;

/** `text` as a feedback report may carry it: one line, credentials redacted, at most MAX_LINE characters. */
export function scrubLine(text: string): string {
  const lines = text.split(/\r?\n/);
  const rest = lines.length - 1;
  let first = lines[0]
    .replace(TOKEN, '[redacted]')
    .replace(HEADER, (_m, name: string, sep: string) => `${name}${sep}[redacted]`)
    .replace(WORD, (_m, marker: string) => `${marker}[redacted]`);
  if (first.length > MAX_LINE) first = first.slice(0, MAX_LINE) + '…';
  return rest > 0 ? `${first} … (${rest} more line${rest === 1 ? '' : 's'})` : first;
}

/**
 * A request path as a feedback report may carry it. Changeling's own API
 * paths name a repository and pull request at most and are kept; a bridged
 * GitHub path is cut after its resource (`contents/…`, `git/…`), since what
 * follows can be a file path or a ref, which are the pull request's content.
 */
export function scrubPath(path: string): string {
  const m = /^(\/api\/gh\/rest\/repos\/[^/]+\/[^/]+\/)([^/]+)(\/.*)?$/.exec(path);
  if (!m) return path;
  const [, repo, resource, rest] = m;
  if (!rest) return path;
  // `pulls/3`, `issues/3`: a number names nothing of the pull request's content.
  if (/^\/\d+$/.test(rest)) return path;
  return `${repo}${resource}/…`;
}
